import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Link,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import InstallationSummary from './InstallationSummary'
import MessageTrafficVolumeRate from 'molecules/MessageTraffic/MessageVolumeRate'
import MessageTrafficVolumeChart from 'molecules/MessageTraffic/MessageVolumeChart'
import Editor from '@monaco-editor/react'
import UUIDChip from 'atoms/UUIDChip/UUIDChip'
import { InstallationComponent, InstallationDetail } from 'common/types/kraken-core/Installation'
import { IntegrationModule } from 'common/types/kraken-core/IntegrationModule'
import { useEffect, useState } from 'react'
import { TrafficResponseWrapper } from 'common/types/kraken-core/TrafficVolume'
import { ROUTES } from 'routing/routes'
import mapping from 'atoms/Icon/mapping'
import { getTraffic } from 'services/kraken-core/messages/messages.service'
import moment from 'moment'
import { toast } from 'react-toastify'
import qs from 'qs'
import { ArrowOutward } from '@mui/icons-material'

export interface InstallationDetailContainerProps {
  installation: InstallationDetail
  integrationModule: IntegrationModule
}

const DEFAULT_TRAFFIC_PERIOD_IN_DAYS = 7
const DEFAULT_TRAFFIC_TIMEFRAME = 'hour'

export default function InstallationDetailContainer(props: InstallationDetailContainerProps) {
  const { installation, integrationModule } = props
  const [activeTab, setActiveTab] = useState<number>(0)
  const [traffic, setTraffic] = useState<TrafficResponseWrapper>({
    isLoading: false,
    traffic: {
      buckets: [],
      count: 0,
      from_date: '',
      to_date: '',
      timeframe: DEFAULT_TRAFFIC_TIMEFRAME,
    },
  })
  const [trafficTimeframe, setTrafficTimeframe] = useState(DEFAULT_TRAFFIC_PERIOD_IN_DAYS)

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
  }

  function getInstallationComponentDetailsPageUrl(component: InstallationComponent) {
    const mapping: any = {
      ConnectionSettings: (uuid: string) => `${ROUTES.CONNECTIONS}/${uuid}`,
      TranslationMap: (uuid: string) => `${ROUTES.TRANSLATION_MAPS}/${uuid}`,
      TranslationSettings: (uuid: string) => `${ROUTES.TRANSLATION_SETTINGS}/${uuid}`,
      GatewaySettings: (uuid: string) => `${ROUTES.GATEWAYS}/${uuid}`,
      Route: (uuid: string) => `${ROUTES.ROUTES}/${uuid}`,
    }
    return mapping[component.model](component.id)
  }

  function getComponentIcon(component: string) {
    return mapping[component]
  }

  function handleTrafficTimeframeChange(
    event: React.MouseEvent<HTMLElement>,
    newTimeframe: number
  ) {
    setTrafficTimeframe(newTimeframe || DEFAULT_TRAFFIC_PERIOD_IN_DAYS)
  }

  function fetchTraffic(installationId: string) {
    getTraffic({
      filters: { installationId: installationId },
      from_date: moment().subtract(trafficTimeframe, 'days').startOf('day').toISOString(),
      to_date: '',
      timeframe: DEFAULT_TRAFFIC_TIMEFRAME,
    })
      .then(response => {
        response.response && setTraffic({ traffic: response.response.data, isLoading: false })
      })
      .catch(error => {
        toast.error('Error getting traffic')
      })
      .finally(() => {
        setTraffic((prev: any) => ({ ...prev, isLoading: false }))
      })
  }

  useEffect(() => {
    if (!installation) return

    setTraffic((prev: any) => ({ ...prev, isLoading: true }))
    fetchTraffic(installation.id)
  }, [installation, trafficTimeframe])

  const messageParams = qs.stringify({
    installation: [
      {
        id: installation.id,
        name: installation.name,
        _type: 'string',
      },
    ],
    created_at_from: moment().subtract(trafficTimeframe, 'days').startOf('day').toISOString(),
  })

  const isLoading = traffic?.isLoading
  const hasTraffic = !!traffic?.traffic?.buckets?.length
  const noTraffic = !isLoading && !hasTraffic

  return (
    <Stack spacing={2}>
      {installation && integrationModule ? (
        <InstallationSummary installation={installation} integrationModule={integrationModule} />
      ) : null}

      <Stack>
        <Tabs sx={{ pl: 2, pr: 2 }} value={activeTab} onChange={handleTabChange}>
          <Tab label="Traffic" />
          <Tab label="Parameters" />
          <Tab label="Components" />
          {/* <Tab label="Rules" /> */}
        </Tabs>

        {activeTab === 0 && (
          <Stack spacing={2}>
            <Paper sx={{ p: 2 }}>
              <Stack p={0}>
                <Stack spacing={2}>
                  <Grid container spacing={2}>
                    <Grid item>
                      <Grid container>
                        <ToggleButtonGroup
                          size="small"
                          exclusive
                          value={trafficTimeframe}
                          onChange={handleTrafficTimeframeChange}
                        >
                          <ToggleButton value={7}>
                            <Typography variant="caption" fontWeight={'bold'}>
                              7 Days
                            </Typography>
                          </ToggleButton>
                          <ToggleButton value={30}>
                            <Typography variant="caption" fontWeight={'bold'}>
                              30 Days
                            </Typography>
                          </ToggleButton>
                          <ToggleButton value={60}>
                            <Typography variant="caption" fontWeight={'bold'}>
                              2 Months
                            </Typography>
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </Grid>
                    </Grid>

                    <Grid item justifyContent="end" alignContent="center">
                      {/* {isLoading && <Loading />} */}
                      {noTraffic && (
                        <Box>
                          <Typography variant="caption" color="textSecondary">
                            There has been no traffic in the last {trafficTimeframe} days...
                          </Typography>
                        </Box>
                      )}
                      {hasTraffic && (
                        <Grid item>
                          <Grid container spacing={1}>
                            <Grid item>
                              <MessageTrafficVolumeRate traffic={traffic} />
                            </Grid>
                            <Grid item>
                              <Link href={`${ROUTES.MESSAGES}?${messageParams}`} target="_blank">
                                <Typography variant="caption" color="primary" fontWeight={'bold'}>
                                  View Messages
                                </Typography>
                                <ArrowOutward fontSize="small" />
                              </Link>
                            </Grid>
                          </Grid>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                  <MessageTrafficVolumeChart traffic={traffic} />
                </Stack>
              </Stack>
            </Paper>
          </Stack>
        )}

        {activeTab === 1 && (
          <Card>
            <CardContent>
              <Stack spacing={2}>
                <Editor
                  value={JSON.stringify(installation.parameters, null, 2)}
                  language="json"
                  height="200px"
                />
              </Stack>
            </CardContent>
          </Card>
        )}

        {activeTab === 2 && installation.components && installation.components.length > 0 && (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Model</TableCell>
                  <TableCell>Name</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {installation.components.map(component => (
                  <TableRow key={component.id}>
                    <TableCell>
                      <UUIDChip
                        uuid={component.id}
                        onClick={() => {
                          window.open(getInstallationComponentDetailsPageUrl(component), '_blank')
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        icon={getComponentIcon(component.model)}
                        label={component.model}
                      />
                    </TableCell>
                    <TableCell>{component.metadata?.name || 'N/A'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Stack>
    </Stack>
  )
}
